'use client';

import type { PaginationState } from '@tanstack/react-table';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
} from '@workspace/ui/lib';
import {
  createParser,
  type HistoryOptions,
  parseAsInteger,
  type UseQueryStatesKeysMap,
  useQueryStates,
  type Values,
} from 'nuqs';
import { type SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

interface TablePaginationKeys {
  page?: string;
  pageSize?: string;
}

function createAllowedPageSizeParser(allowedPageSizes: readonly number[], defaultPageSize: number) {
  const allowedPageSizesSet = new Set(allowedPageSizes);

  return createParser({
    parse(value) {
      const parsed = Number(value);
      if (!Number.isFinite(parsed) || !allowedPageSizesSet.has(parsed)) return null;

      return parsed;
    },
    serialize(value) {
      return String(value);
    },
  }).withDefault(defaultPageSize);
}

function getTablePaginationParsers(
  defaultPageSize: number,
  allowedPageSizes: readonly number[],
  paginationKeys?: TablePaginationKeys
): UseQueryStatesKeysMap {
  return {
    [paginationKeys?.page ?? 'page']: parseAsInteger.withDefault(DEFAULT_PAGE_INDEX),
    [paginationKeys?.pageSize ?? 'pageSize']: createAllowedPageSizeParser(
      allowedPageSizes,
      defaultPageSize
    ),
  };
}

type TablePaginationParsers = ReturnType<typeof getTablePaginationParsers>;
type ReservedPaginationKey = 'page' | 'pageSize';
type TableStateParsers<Parsers extends UseQueryStatesKeysMap> = TablePaginationParsers &
  Omit<Parsers, ReservedPaginationKey>;
type TableStateValues<Parsers extends UseQueryStatesKeysMap> = Values<
  TableStateParsers<Parsers>
> & {
  page: number;
  pageSize: number;
};
type TableStateUpdate<Parsers extends UseQueryStatesKeysMap> = Partial<{
  [Key in keyof TableStateValues<Parsers>]: TableStateValues<Parsers>[Key] | null;
}>;

interface UseNuqsTableStateOptions<Parsers extends UseQueryStatesKeysMap> {
  parsers: Parsers;
  defaultPageSize?: number;
  pageSizeOptions?: readonly number[];
  history?: HistoryOptions;
  paginationKeys?: TablePaginationKeys;
}

interface UseNuqsTableStateResult<Parsers extends UseQueryStatesKeysMap> {
  urlState: TableStateValues<Parsers>;
  setUrlState: (values: TableStateUpdate<Parsers>) => Promise<URLSearchParams>;
  pagination: PaginationState;
  setPagination: (value: SetStateAction<PaginationState>) => void;
  resetPage: () => void;
}

function resolvePageSize(
  pageSize: number,
  allowedPageSizes: readonly number[],
  defaultPageSize: number
) {
  return allowedPageSizes.includes(pageSize) ? pageSize : defaultPageSize;
}

export function useNuqsTableState<Parsers extends UseQueryStatesKeysMap>({
  parsers,
  defaultPageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  history = 'replace',
  paginationKeys,
}: UseNuqsTableStateOptions<Parsers>): UseNuqsTableStateResult<Parsers> {
  const pageKey = paginationKeys?.page ?? 'page';
  const pageSizeKey = paginationKeys?.pageSize ?? 'pageSize';

  const allowedPageSizes = useMemo(() => {
    if (pageSizeOptions.includes(defaultPageSize)) return pageSizeOptions;

    return [...pageSizeOptions, defaultPageSize].sort((a, b) => a - b);
  }, [defaultPageSize, pageSizeOptions]);

  const tableParsers = useMemo(
    () => ({
      ...getTablePaginationParsers(defaultPageSize, allowedPageSizes, paginationKeys),
      ...parsers,
    }),
    [allowedPageSizes, defaultPageSize, paginationKeys, parsers]
  );

  const [rawUrlState, setRawUrlState] = useQueryStates(tableParsers, { history });
  const urlState = useMemo(
    () =>
      ({
        ...rawUrlState,
        page: rawUrlState[pageKey],
        pageSize: resolvePageSize(
          rawUrlState[pageSizeKey] as number,
          allowedPageSizes,
          defaultPageSize
        ),
      }) as TableStateValues<Parsers>,
    [allowedPageSizes, defaultPageSize, pageKey, pageSizeKey, rawUrlState]
  );

  const setUrlState = useCallback(
    (values: TableStateUpdate<Parsers>) => {
      const nextValues = { ...values } as Record<string, unknown>;

      if (pageKey !== 'page' && 'page' in nextValues) {
        nextValues[pageKey] = nextValues.page;
        delete nextValues.page;
      }

      if (pageSizeKey !== 'pageSize' && 'pageSize' in nextValues) {
        nextValues[pageSizeKey] = nextValues.pageSize;
        delete nextValues.pageSize;
      }

      if ('pageSize' in nextValues && nextValues.pageSize != null) {
        nextValues.pageSize = resolvePageSize(
          nextValues.pageSize as number,
          allowedPageSizes,
          defaultPageSize
        );
      }

      if (pageSizeKey in nextValues && nextValues[pageSizeKey] != null) {
        nextValues[pageSizeKey] = resolvePageSize(
          nextValues[pageSizeKey] as number,
          allowedPageSizes,
          defaultPageSize
        );
      }

      return setRawUrlState(nextValues as Parameters<typeof setRawUrlState>[0]);
    },
    [allowedPageSizes, defaultPageSize, pageKey, pageSizeKey, setRawUrlState]
  );

  const pagination = useMemo(
    () => ({
      pageIndex: Math.max(DEFAULT_PAGE_INDEX, urlState.page),
      pageSize: resolvePageSize(urlState.pageSize, allowedPageSizes, defaultPageSize),
    }),
    [allowedPageSizes, defaultPageSize, urlState.page, urlState.pageSize]
  );
  const paginationRef = useRef(pagination);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    const pageSizeFromUrl = rawUrlState[pageSizeKey] as number | null | undefined;
    if (pageSizeFromUrl == null) return;

    if (!allowedPageSizes.includes(pageSizeFromUrl)) {
      void setRawUrlState({ [pageSizeKey]: null } as Parameters<typeof setRawUrlState>[0]);
    }
  }, [allowedPageSizes, pageSizeKey, rawUrlState, setRawUrlState]);

  const setPagination = useCallback(
    (value: SetStateAction<PaginationState>) => {
      const nextPagination = typeof value === 'function' ? value(paginationRef.current) : value;

      void setUrlState({
        page: nextPagination.pageIndex,
        pageSize: resolvePageSize(nextPagination.pageSize, allowedPageSizes, defaultPageSize),
      } as TableStateUpdate<Parsers>);
    },
    [allowedPageSizes, defaultPageSize, setUrlState]
  );

  const resetPage = useCallback(() => {
    void setUrlState({ page: DEFAULT_PAGE_INDEX } as TableStateUpdate<Parsers>);
  }, [setUrlState]);

  return {
    urlState,
    setUrlState,
    pagination,
    setPagination,
    resetPage,
  };
}
